import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  Button,
  Checkbox,
  DialogContentText,
  FormControlLabel,
  useTheme,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { ActivityStatus } from "generated-graphql/graphql";
import { useCreateTierIIReportComment } from "hooks/comments";
import { useActivityLogs } from "hooks/useActivityLogs";
import { useUpdateActivity } from "hooks/activities";
import { useState } from "react";
import { ActivityButtonProps } from ".";
import { useReportDetails } from "../../useReportDetails";

export const Requeue = ({
  activity,
  showWarningPanel = false,
  context = "report",
  tags,
  refetchQueries,
}: ActivityButtonProps & {
  showWarningPanel?: boolean;
  context?: "report" | "cohort";
  tags?: string;
  refetchQueries?: string[];
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data } = useReportDetails();
  const [updateActivity, { loading }] = useUpdateActivity(refetchQueries);
  const [createComment] = useCreateTierIIReportComment();

  // Get error message from activity logs
  const { data: logData } = useActivityLogs(
    activity.id,
    `status:${ActivityStatus.Error}`,
    0,
    1,
    [{ field: "time", sort: "desc" }]
  );

  const errorMessage =
    logData?.activityLogs.items?.[0]?.message ?? null;

  const workflowId = data?.tierIIReport.currentWorkflow?.id;
  const reportId = data?.tierIIReport.id;

  const contextMessage =
    context === "cohort" ? "cohort activities page" : "report details page";
  const tagsToUse = tags ?? data?.tierIIReport.tags;
  const tagsMessage = tagsToUse ? ` Clearing tags: ${tagsToUse}.` : "";

  const requeueAndUpdateCache = async () => {
    await updateActivity({
      variables: {
        id: activity.id,
        input: {
          status: ActivityStatus.Queued,
          message: `Requeued activity from ${contextMessage}.${tagsMessage}`,
        },
      },
      update(cache, { data }) {
        if (data?.updateActivity.status) {
          cache.modify({
            id: cache.identify({
              __typename: "Activity",
              id: activity.id,
            }),
            fields: {
              status() {
                return data?.updateActivity?.status;
              },
            },
          });
        }
      },
    });

    // Add workflow comment with error message
    if (workflowId && reportId && errorMessage) {
      const commentText = `Requeued:\n"${errorMessage}"`;
      await createComment({
        variables: {
          input: {
            reportId,
            workflowId,
            comment: commentText,
          },
        },
      });
    }
  };

  const onClick = () => {
    if (showWarningPanel) {
      setIsDialogOpen(true);
    } else {
      requeueAndUpdateCache();
    }
  };

  return (
    <>
      <Button
        variant="text"
        size="small"
        color="primary"
        loadingPosition="start"
        onClick={onClick}
        startIcon={<PlayArrowIcon />}
        sx={{ minWidth: 100 }}
      >
        Requeue
      </Button>
      <RequeueDialog
        activityId={activity.id}
        isOpen={isDialogOpen}
        onCancel={() => setIsDialogOpen(false)}
        loading={loading}
        context={context}
        onSubmit={() => {
          requeueAndUpdateCache();
          setIsDialogOpen(false);
        }}
      />
    </>
  );
};

interface Props {
  activityId: string;
  onSubmit: () => void;
  onCancel: () => void;
  isOpen: boolean;
  loading: boolean;
  context: "report" | "cohort";
}

const RequeueDialog = ({
  isOpen,
  onCancel,
  onSubmit,
  loading,
  context,
}: Props) => {
  const theme = useTheme();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const handleRequeueButtonClick = () => {
    onSubmit();
  };

  const handleClose = () => {
    setIsConfirmed(false);
    onCancel();
  };

  const subheaderText =
    context === "cohort"
      ? "Making changes now may affect the cohort workflow, which has already started. Some steps cannot be recalled."
      : "Making changes now may affect the report submission, which has already started. Some steps cannot be recalled, such as submitting a payment.";

  const detailsLinkText =
    context === "cohort" ? "cohort details" : "report details";

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      title="Requeue Activity"
      primaryActionButtonProps={{
        onClick: handleRequeueButtonClick,
        loading,
        disabled: !isConfirmed,
        saveText: "Requeue",
        startIcon: null,
      }}
      subheader={subheaderText}
      description={
        <DialogContentText>
          Submit a ticket to{" "}
          <a
            href="https://encamp.slack.com/archives/C02EUTUBC1H"
            style={{ textDecoration: "none" }}
          >
            #engineering-support
          </a>{" "}
          for manual review by an engineer with these{" "}
          <a href={window.location.href} style={{ textDecoration: "none" }}>
            {detailsLinkText}
          </a>
          .
        </DialogContentText>
      }
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={isConfirmed}
            onChange={(e) => setIsConfirmed(e.target.checked)}
            name="confirmed"
            color="primary"
          />
        }
        label="I've reviewed this with an engineer and want to override the safety check"
      />
    </Dialog>
  );
};
